import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Link } from 'react-router-dom';
import bandPhotos from './bandPhotos';

const showNotes = [
  ['01', 'Crowd-first', 'The audience is part of the show, not an afterthought.'],
  ['02', 'Built to move', 'A broad set list keeps the dance floor active all night.'],
  ['03', 'A little theatrical', 'Costume changes and sharp banter make every set feel alive.'],
];

function About() {
  const aboutPhotos = [bandPhotos[7], bandPhotos[3], bandPhotos[2], bandPhotos[8]];

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: 470, md: 540 },
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          bgcolor: '#0b0909',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gridTemplateRows: { xs: 'repeat(2, 1fr)', md: '1fr' },
          }}
        >
          {aboutPhotos.map((photo) => (
            <Box
              key={photo.src}
              component="img"
              src={photo.src}
              alt=""
              width="160"
              height="120"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.05) contrast(1.05)',
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(11, 9, 9, 0.2) 0%, rgba(11, 9, 9, 0.48) 42%, #0b0909 100%)',
          }}
        />
        <Container
          maxWidth="xl"
          className="hero-reveal"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 5 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Typography sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
            Behind the music
          </Typography>
          <Typography
            component="h1"
            variant="h1"
            sx={{
              fontSize: { xs: '4.25rem', sm: '6rem', md: '7rem' },
              lineHeight: 0.88,
              maxWidth: 900,
              textWrap: 'balance',
            }}
          >
            Professionals by day. Showband by night.
          </Typography>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#f1e7d8', color: '#1b130f' }}>
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 8, md: 12 } }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.72fr) minmax(0, 1fr)' },
              gap: { xs: 5, md: 12 },
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography sx={{ color: '#b43a25', fontWeight: 800, mb: 1 }}>
                Our story
              </Typography>
              <Typography
                component="h2"
                variant="h2"
                sx={{
                  color: '#17100d',
                  fontSize: { xs: '3.8rem', sm: '5rem', md: '6rem' },
                  lineHeight: 0.9,
                  textWrap: 'balance',
                }}
              >
                Serious careers. Zero interest in a quiet night.
              </Typography>
            </Box>
            <Box sx={{ borderTop: '3px solid #e95d3f', pt: 3 }}>
              <Typography sx={{ color: '#2f241f', fontSize: { xs: '1.08rem', md: '1.25rem' }, lineHeight: 1.75 }}>
                Malpractice began with a group of doctors, lawyers, engineers, educators, and other professionals who would rather break into song than call it an early night.
              </Typography>
              <Typography sx={{ color: '#2f241f', fontSize: { xs: '1.08rem', md: '1.25rem' }, lineHeight: 1.75, mt: 3 }}>
                We bring musicianship, humor, costume changes, and a little Las Vegas energy to every event. The goal is simple: win over the room and keep people dancing.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#15110f' }}>
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 8, md: 11 } }}
        >
          <Typography
            component="h2"
            variant="h2"
            sx={{ fontSize: { xs: '3.5rem', md: '5rem' }, lineHeight: 0.95, mb: 5 }}
          >
            What the room feels
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              borderTop: '1px solid rgba(248, 241, 231, 0.25)',
            }}
          >
            {showNotes.map(([number, title, copy]) => (
              <Box
                key={number}
                sx={{
                  py: 4,
                  pr: { md: 4 },
                  borderBottom: '1px solid rgba(248, 241, 231, 0.25)',
                  borderRight: { md: number === '03' ? 'none' : '1px solid rgba(248, 241, 231, 0.25)' },
                  pl: { md: number === '01' ? 0 : 4 },
                }}
              >
                <Typography sx={{ color: 'primary.main', fontWeight: 800, mb: 2 }}>
                  {number}
                </Typography>
                <Typography component="h3" variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                  {title}
                </Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {copy}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'secondary.main', color: '#140c09' }}>
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 7, md: 9 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 4,
          }}
        >
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: '3.75rem', md: '6rem' }, lineHeight: 0.9, maxWidth: 760 }}
          >
            Bring the whole show to your event
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            endIcon={<ArrowOutwardIcon />}
            sx={{ bgcolor: '#0b0909', color: '#f8f1e7', flexShrink: 0, '&:hover': { bgcolor: '#33231c' } }}
          >
            Book Malpractice
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default About;
